import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { Search, MapPin, ChevronDown, ShoppingCart, Timer, User2, LogOut } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const LogoMark = () => (
  <svg width="36" height="36" viewBox="0 0 48 48" aria-hidden="true">
    <defs>
      <linearGradient id="z-stack-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#0066FF" />
        <stop offset="100%" stopColor="#06B6D4" />
      </linearGradient>
    </defs>
    <path d="M8 30 L24 38 L40 30 L24 22 Z" fill="url(#z-stack-grad)" opacity="0.95" />
    <path d="M8 22 L24 30 L40 22 L24 14 Z" fill="url(#z-stack-grad)" opacity="0.7" />
    <path d="M8 14 L24 22 L40 14 L24 6 Z" fill="url(#z-stack-grad)" opacity="0.45" />
  </svg>
);

export default function CustomerHeader({ onCartClick, onSearchFocus, searchValue, onSearchChange }) {
  const { user, logout } = useAuth();
  const addr = user?.addresses?.[0];
  const location = addr ? `${addr.label}: ${addr.line1}` : "Add your delivery address";
  const cart = useCart();
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-slate-200"
      data-testid="customer-header"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        {/* top row */}
        <div className="h-16 sm:h-20 flex items-center gap-3 sm:gap-6">
          <Link to="/app" className="flex items-center gap-2.5 shrink-0" data-testid="header-logo">
            <LogoMark />
            <div className="hidden sm:block leading-tight">
              <div className="font-extrabold text-xl tracking-tight text-slate-900">STACK</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold -mt-0.5">⚡ 10-min delivery</div>
            </div>
          </Link>

          {/* location */}
          <button
            onClick={() => navigate("/profile")}
            className="hidden md:flex items-start text-left shrink-0 hover:bg-slate-50 rounded-lg px-2 py-1.5"
            data-testid="location-selector"
          >
            <MapPin size={16} className="text-slate-700 mt-0.5 mr-1" />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
                Delivery in 10 mins <ChevronDown size={11} />
              </div>
              <div className="text-sm font-semibold text-slate-900 max-w-[200px] truncate">{location}</div>
            </div>
          </button>

          {/* search */}
          <div className="flex-1 relative" data-testid="header-search-wrap">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={searchValue || ""}
              onChange={(e) => onSearchChange?.(e.target.value)}
              onFocus={onSearchFocus}
              placeholder='Search "milk", "paracetamol", "chips"…'
              className="w-full h-11 sm:h-12 pl-11 pr-4 rounded-xl bg-slate-50 border border-slate-200 focus:border-slate-900 focus:bg-white focus:outline-none text-sm sm:text-[15px] placeholder:text-slate-400 text-slate-900 font-medium transition"
              data-testid="search-product-input"
            />
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="hidden sm:inline-flex items-center gap-2 rounded-lg hover:bg-slate-100 px-3 py-2" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 text-white font-bold inline-flex items-center justify-center text-sm">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div className="text-left leading-tight">
                    <div className="text-[11px] text-slate-500">Hi,</div>
                    <div className="text-sm font-semibold text-slate-900 max-w-[90px] truncate">{user.name?.split(" ")[0]}</div>
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-56">
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User2 size={14} className="mr-2" /> Profile & addresses</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">Orders</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout"><LogOut size={14} className="mr-2" /> Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={() => navigate("/login?role=customer")}
                className="hidden sm:inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800"
                data-testid="header-login-btn"
              >
                Sign in
              </button>
            )}

            <button
              onClick={onCartClick}
              className="relative inline-flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-3 sm:px-4 h-10 sm:h-11 font-semibold text-sm"
              data-testid="cart-toggle-btn"
            >
              <ShoppingCart size={16} />
              <span className="hidden sm:inline">My Cart</span>
              {cart.count > 0 && (
                <span className="ml-1 bg-white/20 rounded-full px-1.5 text-[11px] font-bold min-w-[22px] text-center" data-testid="cart-count-badge">
                  {cart.count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* delivery strip (mobile) */}
        <div className="md:hidden pb-2 flex items-center gap-2 text-xs">
          <Timer size={12} className="text-emerald-600" />
          <span className="font-bold text-slate-900">10 minute delivery</span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-600 truncate">to {location}</span>
        </div>
      </div>
    </header>
  );
}
